// Shared utility for picking voice lines without repeating until the
// pool is exhausted. State is module-level and persists for the whole
// session — across matches as well as within them — so a character
// who fires the same trigger in back-to-back matches (e.g. the
// opponent profile's `opponent_lose` after consecutive wins) doesn't
// repeat its line. Reloading the page is the natural reset point.
// `resetVoiceLinePool()` is retained for tests / dev convenience but
// is no longer called during normal play.
//
// Picks are randomized within the unseen subset. When every line in a
// requested pool has been used, we refresh by un-marking that pool's
// lines and pick from the refreshed pool — but exclude `lastShown` so
// we never speak the same line on consecutive picks across the
// refresh boundary.

const seenLines = new Set<string>();
let lastShown: string | null = null;

/** Pick one line from the given pool, avoiding lines already shown
 *  this match. Returns null only when `lines` is empty. */
export function pickVoiceLine(lines: readonly string[]): string | null {
  if (lines.length === 0) return null;

  // First pass: unseen lines.
  let pool = lines.filter(l => !seenLines.has(l));

  // Pool exhausted — refresh by removing THIS pool's lines from the
  // seen set. Keep `lastShown` excluded so we don't immediately repeat
  // ourselves on the refresh pick. Edge case: a single-line pool
  // bypasses the lastShown filter so it can repeat (otherwise we'd
  // have nothing to pick from).
  if (pool.length === 0) {
    for (const l of lines) seenLines.delete(l);
    pool = lastShown !== null ? lines.filter(l => l !== lastShown) : [...lines];
    if (pool.length === 0) pool = [...lines];
  }

  const pick = pool[Math.floor(Math.random() * pool.length)];
  seenLines.add(pick);
  lastShown = pick;
  return pick;
}

/** Reset the seen-line tracking. Not called during normal play (the
 *  pool persists across matches by design); kept for tests and for
 *  any future hard-reset hook. */
export function resetVoiceLinePool(): void {
  seenLines.clear();
  lastShown = null;
}

/** Append a voice-line entry to the match game log.
 *  Format:
 *    speaker — `Character name: "line text"`
 *    silent  — `Character name: line text`
 *  Quotes for speakers; non-speakers render their narration line
 *  plain so it reads as observed action rather than speech. */
export function logVoiceLine(log: string[], name: string, line: string, canSpeak: boolean): void {
  log.push(canSpeak ? `${name}: "${line}"` : `${name}: ${line}`);
}
